import { useApolloClient } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import { createContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useSearch, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Container, Paragraph, Spinner, Table, tokens } from '@treams/treams-ui';

import IntercomTarget from '../../packages/kakapo-ui-application/common/containers/IntercomTarget';
import CycleMomentSendReminderModal from '../../packages/kakapo-ui-kit/components/cycles/CycleMomentSendReminderModal';
import CycleMomentSkipModal, {
	CycleMomentInstance,
} from '../../packages/kakapo-ui-kit/components/cycles/CycleMomentSkipModal';
import { isCycleDue } from '../../packages/kakapo-ui-kit/components/cycles/CycleMomentsPreview';
import InboxManagerCsvExport from './InboxManagerCsvExport';
import InboxManagerHeader, { InboxManagerTableHeader } from './InboxManagerHeader';
import InboxManagerItem from './InboxManagerItem';
import InboxManagerSidebar from './InboxManagerSidebar';

const Split = styled.div`
	display: flex;
	flex-direction: row;
`;

const Main = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	flex-shrink: 1;
	min-width: 0;
`;

const Sidebar = styled(Box)`
	background: white;
	border-left: 1px solid ${(props) => props.theme.tokens.border.subtle};
	min-height: calc(100vh - 71px);
	flex-shrink: 0;
`;

export const ReminderModalContext = createContext<{
	onSendReminder: (instance: graphql.MomentInstance) => void;
}>({
	onSendReminder: () => {},
});

export const SkipCycleModalContext = createContext<{
	onShowSkip: (moments: CycleMomentInstance[]) => void;
	isFromInbox: boolean;
}>({
	onShowSkip: () => {},
	isFromInbox: false,
});

export function InboxManagerContainerWithoutSidebar(props: { header?: boolean }) {
	const { t } = useTranslation();

	const roles = useViewerRoles();

	const formik = useFormik({
		initialValues: {
			status: [graphql.UserStatus.Active],
			query: '',
			groupIDs: [],
			cycleTemplateIDs: [],
			jobTitleIDs: [],
			recurse: false,
			onlyDue: false,
			startDate: moment().startOf('day').subtract(2, 'months').toDate(),
			endDate: moment().startOf('day').add(5, 'months').toDate(),
		},
		onSubmit: () => {},
	});

	const [reminderModalMoment, setReminderModalMoment] = useState<
		graphql.MomentInstance | undefined
	>(undefined);
	const [skipCycleMoments, setSkipCycleMoments] = useState<CycleMomentInstance[] | undefined>(
		undefined,
	);
	const [exporting, setExporting] = useState(false);
	const [sidebar, setSidebar] = useState(false);
	const [selected, setSelected] = useState<string | undefined>();
	const [details, setDetails] = useState(new Map());

	const dateRange: [Date, Date] = [formik.values.startDate, formik.values.endDate];

	const searchFilters: string[] = [];

	if (formik.values.groupIDs.length > 0) {
		searchFilters.push(
			'(' + formik.values.groupIDs.map((id: string) => `groupID:${id}`).join(' OR ') + ')',
		);
	}

	if (formik.values.cycleTemplateIDs.length > 0) {
		searchFilters.push(
			'(' +
				formik.values.cycleTemplateIDs
					.map((id: string) => (id === '0' ? 'cycles:0' : `cycleTemplateIDs:${id}`))
					.join(' OR ') +
				')',
		);
	}

	if (formik.values.jobTitleIDs.length > 0) {
		searchFilters.push(
			'(' +
				formik.values.jobTitleIDs
					.map((id: string) => (id === '0' ? 'jobs:0' : `jobTitleIDs:${id}`))
					.join(' OR ') +
				')',
		);
	}

	const { loading, results } = useSearch(formik.values.query, {
		filters: searchFilters.join(' AND '),
		status: formik.values.status,
	});

	const query = graphql.useManagerInboxQuery({
		fetchPolicy: 'network-only',
		variables: {
			recurse: formik.values.recurse,
		},
		skip: roles.isWorkspaceSuperuser,
	});

	const managees = new Set(query.data?.viewer.managees.map(({ id }) => id) ?? []);

	const users = _.sortBy(results ?? [], (user) => `${user.firstName} ${user.lastName}`).filter(
		({ id }) => roles.isWorkspaceSuperuser || managees.has(id),
	);

	const filtered = users.filter((user) => {
		if (!formik.values.onlyDue) {
			return true;
		}

		const detail = details.get(user.id);

		if (!detail) {
			return true;
		}

		return isCycleDue(detail, roles.isWorkspaceSuperuser, dateRange);
	});

	const client = useApolloClient();

	useEffect(() => {
		setSelected(undefined);
	}, [formik.values.startDate, formik.values.endDate]);

	const fetchedDetailIdsRef = useRef<Set<string>>(new Set());
	const unmountedRef = useRef(false);

	useEffect(
		() => () => {
			unmountedRef.current = true;
		},
		[],
	);

	useEffect(() => {
		const idsToFetch = users
			.map((user) => user.id)
			.filter((id) => !fetchedDetailIdsRef.current.has(id));

		if (idsToFetch.length === 0) return;

		idsToFetch.forEach((id) => fetchedDetailIdsRef.current.add(id));

		// Guard on unmount only: `users` is a new array every render, so this
		// effect re-runs constantly. Cancelling in-flight loops on re-run would
		// abandon the claimed IDs forever (they stay in fetchedDetailIdsRef).
		// Concurrent runs are safe because each run only fetches the IDs it
		// claimed above.
		(async () => {
			const chunks = _.chunk(idsToFetch, 10);
			for (const ids of chunks) {
				if (unmountedRef.current) return;

				try {
					const response = await client.query<graphql.ManagerInboxDetailQuery>({
						query: graphql.ManagerInboxDetailDocument,
						fetchPolicy: 'network-only',
						variables: { ids },
					});
					if (unmountedRef.current) return;

					setDetails((details) => {
						const copy = new Map(details);
						if (!response.data) return copy;
						for (const user of response.data.usersByIDs) {
							copy.set(user.id, user);
						}
						return copy;
					});
				} catch {
					// Allow a retry on next effect run if the request failed.
					ids.forEach((id) => fetchedDetailIdsRef.current.delete(id));
				}
			}
		})();
	}, [users, client]);

	const updateDetailForUser = async (userID: string) => {
		const response = await client.query<graphql.ManagerInboxDetailQuery>({
			query: graphql.ManagerInboxDetailDocument,
			fetchPolicy: 'network-only',
			variables: { ids: [userID] },
		});

		const updatedUser = response.data ? response.data.usersByIDs[0] : undefined;

		setDetails((details) => {
			const copy = new Map(details);
			copy.set(userID, updatedUser);
			return copy;
		});

		setSkipCycleMoments(undefined);
	};

	return (
		<SkipCycleModalContext.Provider
			value={{
				onShowSkip: (moments) => {
					setSkipCycleMoments(moments);
				},
				isFromInbox: true,
			}}
		>
			<ReminderModalContext.Provider
				value={{
					onSendReminder: (momentInstance) => {
						setReminderModalMoment(momentInstance);
					},
				}}
			>
				{skipCycleMoments && (
					<CycleMomentSkipModal
						moments={skipCycleMoments}
						onDismiss={(userId) =>
							userId ? updateDetailForUser(userId) : setSkipCycleMoments(undefined)
						}
					/>
				)}

				{reminderModalMoment && (
					<CycleMomentSendReminderModal
						instance={reminderModalMoment}
						onDismiss={() => setReminderModalMoment(undefined)}
					/>
				)}
				<IntercomTarget target="manager-inbox" block>
					<Split>
						<Main>
							<Container wide={1000} mt={`${tokens.gap.page}px`} mb={`${tokens.gap.page}px`}>
								<InboxManagerHeader
									sidebar={sidebar}
									header={props.header}
									toggleSidebar={() => setSidebar(!sidebar)}
								/>
								{loading || query.loading ? (
									<Spinner centered />
								) : users.length === 0 ? (
									<Box px={4}>
										<Paragraph textAlign="center">{t('employees.search.empty')}</Paragraph>
									</Box>
								) : (
									<Table>
										<InboxManagerTableHeader />
										{filtered.map((user) => (
											<InboxManagerItem
												key={user.id}
												user={user}
												dateRange={dateRange}
												details={details}
												selected={selected}
												setSelected={setSelected}
											/>
										))}
									</Table>
								)}
							</Container>
						</Main>
						{sidebar && (
							<Sidebar p={24}>
								<FormikProvider value={formik}>
									<InboxManagerSidebar onExport={() => setExporting(true)} />
								</FormikProvider>
							</Sidebar>
						)}
					</Split>
				</IntercomTarget>
				{exporting && (
					<InboxManagerCsvExport
						ids={filtered.map(({ id }) => id)}
						dateRange={dateRange}
						onFinish={() => setExporting(false)}
					/>
				)}
			</ReminderModalContext.Provider>
		</SkipCycleModalContext.Provider>
	);
}
